import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type {
  Reconciliation,
  ReconciliationMatch,
  BankStatementLine,
  GLTransaction,
  ReconciliationStatus,
  EnhancedGLAccount,
} from "../types";

/**
 * Reconciliation ViewStore
 * Implements side-by-side bank statement vs GL matching
 * with AI-powered match suggestions
 */
class ReconciliationStore {
  rootStore: RootStore;

  // ==================== View State ====================

  selectedAccountId: string | null = null;
  selectedReconciliationId: string | null = null;
  viewMode: "list" | "reconcile" = "list";

  // ==================== Match State ====================

  selectedGLTransactionIds: Set<string> = new Set();
  selectedStatementLineIds: Set<string> = new Set();
  pendingMatches: ReconciliationMatch[] = [];

  // ==================== Filtering ====================

  showClearedItems = false; // When true, show all items (matched and unmatched)
  amountTolerance = 0.01; // For fuzzy matching
  dateRangeDays = 5; // Days to look for matches

  // ==================== Historical View State ====================

  viewingHistoricalPeriod = false;
  historicalPeriodDate: string | null = null; // YYYY-MM format

  // ==================== UI State ====================

  isLoading = false;
  isSaving = false;
  isAutoMatching = false;
  autoMatchProgress = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed: Data Access ====================

  get reconciliationsStore() {
    return (this.rootStore as any).reconciliationsStore;
  }

  get matchesStore() {
    return (this.rootStore as any).reconciliationMatchesStore;
  }

  get statementLinesStore() {
    return (this.rootStore as any).bankStatementLinesStore;
  }

  get glTransactionsStore() {
    return (this.rootStore as any).glTransactionsStore;
  }

  get glAccountsStore() {
    return (this.rootStore as any).glAccountsStore;
  }

  get reconciliations(): Reconciliation[] {
    return (this.reconciliationsStore?.entities as Reconciliation[]) || [];
  }

  get matches(): ReconciliationMatch[] {
    return (this.matchesStore?.entities as ReconciliationMatch[]) || [];
  }

  get statementLines(): BankStatementLine[] {
    return (this.statementLinesStore?.entities as BankStatementLine[]) || [];
  }

  get glTransactions(): GLTransaction[] {
    return (this.glTransactionsStore?.entities as GLTransaction[]) || [];
  }

  get bankAccounts(): EnhancedGLAccount[] {
    const accounts = (this.glAccountsStore?.entities as EnhancedGLAccount[]) || [];
    return accounts.filter((a) => a.isBankAccount);
  }

  // ==================== Computed: Current Reconciliation ====================

  get currentReconciliation(): Reconciliation | null {
    if (!this.selectedReconciliationId) return null;
    return (
      this.reconciliations.find(
        (r) => r.reconciliationId === this.selectedReconciliationId
      ) || null
    );
  }

  get currentStatementLines(): BankStatementLine[] {
    if (!this.selectedReconciliationId) return [];
    let lines = this.statementLines.filter(
      (l) => l.reconciliationId === this.selectedReconciliationId
    );
    // If showClearedItems is false, only show unmatched items
    if (!this.showClearedItems) {
      lines = lines.filter((l) => !l.isMatched);
    }
    return lines;
  }

  /**
   * Get only cleared (matched) statement lines
   */
  get clearedStatementLines(): BankStatementLine[] {
    if (!this.selectedReconciliationId) return [];
    return this.statementLines.filter(
      (l) => l.reconciliationId === this.selectedReconciliationId && l.isMatched
    );
  }

  /**
   * Get only uncleared (unmatched) statement lines
   */
  get unclearedStatementLines(): BankStatementLine[] {
    if (!this.selectedReconciliationId) return [];
    return this.statementLines.filter(
      (l) => l.reconciliationId === this.selectedReconciliationId && !l.isMatched
    );
  }

  get currentGLTransactions(): GLTransaction[] {
    if (!this.selectedAccountId) return [];
    return this.glTransactions.filter(
      (t) => t.accountId === this.selectedAccountId
    );
  }

  get currentMatches(): ReconciliationMatch[] {
    if (!this.selectedReconciliationId) return [];
    return this.matches.filter(
      (m) => m.reconciliationId === this.selectedReconciliationId
    );
  }

  // ==================== Computed: Status ====================

  get reconciliationStatus(): {
    matched: number;
    unmatched: number;
    total: number;
    percentComplete: number;
  } {
    const total = this.currentStatementLines.length;
    const matched = this.currentStatementLines.filter((l) => l.isMatched).length;
    const unmatched = total - matched;
    const percentComplete = total > 0 ? (matched / total) * 100 : 0;

    return { matched, unmatched, total, percentComplete };
  }

  get isBalanced(): boolean {
    const rec = this.currentReconciliation;
    return rec ? Math.abs(rec.difference) < 0.01 : false;
  }

  // ==================== Computed: Auto-Match Suggestions ====================

  get autoMatchSuggestions(): Array<{
    statementLine: BankStatementLine;
    glTransaction: GLTransaction;
    confidence: number;
    reason: string;
  }> {
    const suggestions: Array<{
      statementLine: BankStatementLine;
      glTransaction: GLTransaction;
      confidence: number;
      reason: string;
    }> = [];

    const unmatchedLines = this.currentStatementLines.filter((l) => !l.isMatched);
    const unmatchedGL = this.currentGLTransactions.filter(
      (t) => !this.isGLTransactionMatched(t.transactionId)
    );

    for (const line of unmatchedLines) {
      const lineAmount = line.isDebit ? -line.amount : line.amount;

      for (const tx of unmatchedGL) {
        const txAmount = tx.creditAmount - tx.debitAmount;

        // Exact amount match
        if (Math.abs(lineAmount - txAmount) < this.amountTolerance) {
          const dateDiff = Math.abs(
            new Date(line.statementDate).getTime() -
              new Date(tx.transactionDate).getTime()
          );
          const daysDiff = dateDiff / (1000 * 60 * 60 * 24);

          if (daysDiff <= this.dateRangeDays) {
            let confidence = 90 - daysDiff * 2;

            // Check for reference match
            if (
              line.reference &&
              tx.reference &&
              line.reference.includes(tx.reference)
            ) {
              confidence = 98;
            }

            suggestions.push({
              statementLine: line,
              glTransaction: tx,
              confidence: Math.min(100, confidence),
              reason:
                confidence >= 95
                  ? "Exact match with reference"
                  : `Amount match within ${daysDiff.toFixed(0)} days`,
            });
          }
        }
      }
    }

    return suggestions.sort((a, b) => b.confidence - a.confidence);
  }

  // ==================== Actions: Navigation ====================

  selectAccount = (accountId: string | null) => {
    this.selectedAccountId = accountId;
    this.selectedReconciliationId = null;
    this.viewMode = "list";
    this.clearSelections();
  };

  selectReconciliation = (reconciliationId: string | null) => {
    this.selectedReconciliationId = reconciliationId;
    this.viewMode = reconciliationId ? "reconcile" : "list";
    this.clearSelections();
  };

  startNewReconciliation = async (accountId: string, statementDate: string) => {
    this.isSaving = true;
    try {
      const newRec = await this.reconciliationsStore?.create({
        accountId,
        statementDate,
        status: "in_progress" as ReconciliationStatus,
      });

      if (newRec) {
        runInAction(() => {
          this.selectedReconciliationId = newRec.reconciliationId;
          this.viewMode = "reconcile";
        });
      }
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  // ==================== Actions: Selection ====================

  toggleGLTransactionSelection = (transactionId: string) => {
    if (this.selectedGLTransactionIds.has(transactionId)) {
      this.selectedGLTransactionIds.delete(transactionId);
    } else {
      this.selectedGLTransactionIds.add(transactionId);
    }
  };

  toggleStatementLineSelection = (lineId: string) => {
    if (this.selectedStatementLineIds.has(lineId)) {
      this.selectedStatementLineIds.delete(lineId);
    } else {
      this.selectedStatementLineIds.add(lineId);
    }
  };

  clearSelections = () => {
    this.selectedGLTransactionIds.clear();
    this.selectedStatementLineIds.clear();
  };

  // ==================== Actions: Matching ====================

  createMatch = async () => {
    if (
      this.selectedGLTransactionIds.size === 0 ||
      this.selectedStatementLineIds.size === 0
    ) {
      return;
    }

    this.isSaving = true;
    try {
      const glTotal = Array.from(this.selectedGLTransactionIds).reduce(
        (sum, id) => {
          const tx = this.glTransactions.find((t) => t.transactionId === id);
          return sum + (tx ? tx.creditAmount - tx.debitAmount : 0);
        },
        0
      );

      const statementTotal = Array.from(this.selectedStatementLineIds).reduce(
        (sum, id) => {
          const line = this.statementLines.find((l) => l.lineId === id);
          return sum + (line ? (line.isDebit ? -line.amount : line.amount) : 0);
        },
        0
      );

      const match: Partial<ReconciliationMatch> = {
        reconciliationId: this.selectedReconciliationId!,
        matchType: "manual",
        glTransactionIds: Array.from(this.selectedGLTransactionIds),
        glTotal,
        statementLineIds: Array.from(this.selectedStatementLineIds),
        statementTotal,
        confidence: 100,
        matchReason: "Manual match",
        isApproved: true,
      };

      await this.matchesStore?.create(match);

      // Mark statement lines as matched
      for (const lineId of this.selectedStatementLineIds) {
        await this.statementLinesStore?.patch(lineId, { isMatched: true });
      }

      this.clearSelections();
      await this.loadData();
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  acceptSuggestion = async (suggestion: {
    statementLine: BankStatementLine;
    glTransaction: GLTransaction;
    confidence: number;
    reason: string;
  }) => {
    this.selectedGLTransactionIds.clear();
    this.selectedStatementLineIds.clear();
    this.selectedGLTransactionIds.add(suggestion.glTransaction.transactionId);
    this.selectedStatementLineIds.add(suggestion.statementLine.lineId);
    await this.createMatch();
  };

  runAutoMatch = async () => {
    this.isAutoMatching = true;
    this.autoMatchProgress = 0;

    try {
      const suggestions = this.autoMatchSuggestions.filter(
        (s) => s.confidence >= 95
      );
      const total = suggestions.length;

      for (let i = 0; i < suggestions.length; i++) {
        await this.acceptSuggestion(suggestions[i]);
        runInAction(() => {
          this.autoMatchProgress = ((i + 1) / total) * 100;
        });
      }
    } finally {
      runInAction(() => {
        this.isAutoMatching = false;
        this.autoMatchProgress = 0;
      });
    }
  };

  // ==================== Actions: View Toggle ====================

  /**
   * Toggle showing cleared/matched items
   */
  toggleShowClearedItems = () => {
    this.showClearedItems = !this.showClearedItems;
  };

  /**
   * Set show cleared items state
   */
  setShowClearedItems = (show: boolean) => {
    this.showClearedItems = show;
  };

  // ==================== Actions: Historical View ====================

  /**
   * Get list of available historical reconciliation periods
   */
  get historicalReconciliations(): Reconciliation[] {
    return this.reconciliations.filter(
      (r) => r.status === "approved" && r.accountId === this.selectedAccountId
    ).sort((a, b) => {
      // Sort by statement date descending
      return new Date(b.statementDate).getTime() - new Date(a.statementDate).getTime();
    });
  }

  /**
   * Check if we're viewing a historical (read-only) period
   */
  get isReadOnlyMode(): boolean {
    if (this.viewingHistoricalPeriod) return true;
    const rec = this.currentReconciliation;
    return rec !== null && rec.status === "approved";
  }

  /**
   * Enter historical view mode
   */
  enterHistoricalView = (periodDate: string) => {
    this.viewingHistoricalPeriod = true;
    this.historicalPeriodDate = periodDate;

    // Find and select the reconciliation for this period
    const historicalRec = this.reconciliations.find(
      (r) => r.statementDate.startsWith(periodDate) &&
             r.accountId === this.selectedAccountId &&
             r.status === "approved"
    );

    if (historicalRec) {
      this.selectedReconciliationId = historicalRec.reconciliationId;
    }

    // Automatically show cleared items in historical view
    this.showClearedItems = true;
  };

  /**
   * Exit historical view mode
   */
  exitHistoricalView = () => {
    this.viewingHistoricalPeriod = false;
    this.historicalPeriodDate = null;
    this.showClearedItems = false;
  };

  /**
   * Get the period display text for a reconciliation
   */
  getPeriodDisplay = (rec: Reconciliation): string => {
    const date = new Date(rec.statementDate);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  // ==================== Actions: Reconciliation Workflow ====================

  completeReconciliation = async () => {
    if (!this.isBalanced) {
      throw new Error("Cannot complete - reconciliation is not balanced");
    }

    this.isSaving = true;
    try {
      await this.reconciliationsStore?.patch(this.selectedReconciliationId!, {
        status: "pending_review" as ReconciliationStatus,
        reconciledAt: new Date().toISOString(),
      });

      await this.loadData();
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  approveReconciliation = async () => {
    this.isSaving = true;
    try {
      await this.reconciliationsStore?.patch(this.selectedReconciliationId!, {
        status: "approved" as ReconciliationStatus,
        approvedAt: new Date().toISOString(),
      });

      await this.loadData();
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  // ==================== Actions: Data Loading ====================

  loadData = async () => {
    this.isLoading = true;
    try {
      await Promise.all([
        this.reconciliationsStore?.find({ query: { $limit: 100 } }),
        this.glAccountsStore?.find({ query: { $limit: 100 } }),
      ]);

      if (this.selectedReconciliationId) {
        await Promise.all([
          this.statementLinesStore?.find({
            query: {
              reconciliationId: this.selectedReconciliationId,
              $limit: 1000,
            },
          }),
          this.matchesStore?.find({
            query: {
              reconciliationId: this.selectedReconciliationId,
              $limit: 1000,
            },
          }),
        ]);
      }

      if (this.selectedAccountId) {
        await this.glTransactionsStore?.find({
          query: { accountId: this.selectedAccountId, $limit: 1000 },
        });
      }
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // ==================== Helper Methods ====================

  private isGLTransactionMatched(transactionId: string): boolean {
    return this.currentMatches.some((m) =>
      m.glTransactionIds.includes(transactionId)
    );
  }
}

export default ReconciliationStore;
